import React, { useEffect, useState, useMemo } from 'react';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import db from '../lib/database';
import { useStore } from '../store/StoreContext';
import {
  getClientStatuses,
  getEstimateStatuses,
  getOperationsStatuses,
  getStatusLabel,
} from '../lib/constants';

// ActivityLog — per-record audit feed
//
// Renders the chronological list of changes for a specific
// (tableName, recordId) — e.g., everything that's been edited on a
// single client, estimate, or move_job. Sourced from the audit_log
// table populated by Postgres AFTER triggers (sql/030_audit_log.sql).
//
// Use case: accountability + sales tracking. "Who marked this lead
// inactive?" "When did the crew assignment change?" The toast
// notifications tell the actor they made a change; this surface
// tells everyone else they made it, persisted.
//
// Props:
//   - tableName: 'clients' | 'estimates' | 'move_jobs'
//   - recordId: the row's id (uuid as text)
//   - title: optional header label (defaults to "Activity")
//   - limit: max entries to fetch (default 50)
//
// Implementation notes:
// • Each audit row's `changes` JSON is rendered field-by-field. For
//   known fields we apply a friendly label + value formatter (status
//   keys get resolved to display labels via constants.js helpers).
//   Unknown fields fall back to title-cased names + raw values.
// • Long string values are truncated. UUID-array fields (crew_ids,
//   truck_ids) currently render as "Updated [field]" without
//   resolving names — punted to v2 to avoid coupling this component
//   to crew/truck lookups.
// • Bypassed updates (only updated_at changed) are filtered at the
//   trigger level, so the list here is always meaningful.

// ──────────────────────────────────────────────────────────────────────
// Field display
// ──────────────────────────────────────────────────────────────────────

// Friendly labels for known fields. Unknown fields fall back to a
// title-cased version of the raw column name.
// Exported so the global Activity page (AuditPage.jsx) renders the same
// field labels + value formats as the per-record sidebar feed.
export const FIELD_LABELS = {
  status: 'Status',
  name: 'Name',
  phone: 'Phone',
  email: 'Email',
  from_address: 'From address',
  to_address: 'To address',
  additional_stops: 'Additional stops',
  move_date: 'Move date',
  walkthrough_date: 'Walkthrough date',
  time: 'Time',
  duration: 'Duration',
  crew_ids: 'Crew',
  truck_ids: 'Trucks',
  leader_id: 'Lead',
  estimate_id: 'Estimate',
  estimated_hours: 'Estimated hours',
  hourly_rate: 'Hourly rate',
  travel_fee: 'Travel fee',
  materials_fee: 'Materials fee',
  hold_fee: 'Hold fee',
  total_estimate: 'Total estimate',
  packing_needed: 'Packing',
  heavy_items: 'Heavy items',
  special_notes: 'Special notes',
  internal_notes: 'Internal notes',
  dispatcher_notes: 'Dispatch notes',
  crew_recap_notes: 'Recap notes',
  damage_notes: 'Damage notes',
  actual_start_time: 'Actual start',
  actual_end_time: 'Actual end',
  actual_hours: 'Actual hours',
  actual_lunch_minutes: 'Lunch (min)',
  customer_rating: 'Customer rating',
  virtual_walkthrough: 'Virtual walkthrough',
  from_apartment: 'From apartment',
  to_apartment: 'To apartment',
  from_apartment_floor: 'From floor',
  to_apartment_floor: 'To floor',
  from_sqft: 'From sqft',
  to_sqft: 'To sqft',
  from_levels: 'From levels',
  to_levels: 'To levels',
  from_stairs: 'From stairs',
  to_stairs: 'To stairs',
  from_elevator: 'From elevator',
  to_elevator: 'To elevator',
  crew_size_needed: 'Crew size',
  trucks_needed: 'Trucks needed',
  type: 'Type',
  job_type: 'Job type',
  job_label: 'Job label',
  recap_submitted_at: 'Recap submitted',
  phase_sequence: 'Phase sequence',
  trip_tags: 'Trip tags',
  is_admin: 'Admin',
};

export function fieldLabel(field) {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  // Fallback: title-case from snake_case. "from_apartment_floor" → "From apartment floor"
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Format a single value (the `from` or `to` of a diff) for display.
// `field` lets us apply field-specific formatting (status key → label).
export function formatValue(field, value, ctx) {
  // null / undefined / empty-string sentinel for "no value"
  if (value === null || value === undefined || value === '') return '—';

  // Status fields: resolve key → display label via constants
  if (field === 'status') {
    return getStatusLabel(ctx.allStatuses, value) || String(value);
  }

  // Booleans
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  // Arrays (crew_ids, truck_ids, trip_tags, planned_phases, phones…).
  // UUID resolution to names is a v2 feature. Arrays of OBJECTS used to
  // fall through to join() and render "[object Object], [object Object]"
  // (QA M2 re-verify NEW-7 — seen on planned_phases) — summarize by count
  // plus the most telling key instead.
  if (Array.isArray(value)) {
    if (value.length === 0) return 'none';
    if (field === 'crew_ids' || field === 'truck_ids') {
      return `${value.length} ${value.length === 1 ? 'item' : 'items'}`;
    }
    if (typeof value[0] === 'object' && value[0] !== null) {
      const tags = value
        .map(v => v?.kind || v?.label || v?.name || v?.number || null)
        .filter(Boolean);
      const summary = tags.length === value.length ? `: ${tags.join(', ')}` : '';
      const s = `${value.length} ${value.length === 1 ? 'entry' : 'entries'}${summary}`;
      return s.length > 60 ? s.slice(0, 57) + '…' : s;
    }
    const joined = value.join(', ');
    return joined.length > 60 ? joined.slice(0, 57) + '…' : joined;
  }

  // Plain objects: compact JSON, truncated — never "[object Object]".
  if (typeof value === 'object') {
    try {
      const s = JSON.stringify(value);
      return s.length > 60 ? s.slice(0, 57) + '…' : s;
    } catch { return 'object'; }
  }

  // ISO date/time
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      const d = parseISO(value);
      if (!isNaN(d)) return format(d, 'MMM d, yyyy');
    } catch (_) {}
  }

  // Numbers
  if (typeof value === 'number') {
    // Money-ish fields get $
    if (/_(fee|estimate|rate|total)$/.test(field)) return `$${value.toFixed(2)}`;
    return String(value);
  }

  // Long strings get truncated
  const s = String(value);
  if (s.length > 60) return s.slice(0, 57) + '…';
  return s;
}

// ──────────────────────────────────────────────────────────────────────
// Render a single audit entry
// ──────────────────────────────────────────────────────────────────────

function ActivityEntry({ row, ctx }) {
  const when = useMemo(() => {
    try {
      return formatDistanceToNow(parseISO(row.occurred_at), { addSuffix: true });
    } catch {
      return row.occurred_at;
    }
  }, [row.occurred_at]);

  const whenAbs = useMemo(() => {
    try {
      return format(parseISO(row.occurred_at), 'MMM d, yyyy h:mm a');
    } catch {
      return row.occurred_at;
    }
  }, [row.occurred_at]);

  const actor = row.user_name || (row.user_email ? row.user_email.split('@')[0] : null) || 'System';

  // Build a list of "label: from → to" rows for an update operation, or
  // a single summary for insert/delete.
  let body;
  if (row.operation === 'insert') {
    body = <div className="text-[12px] text-slate-500 italic">Created</div>;
  } else if (row.operation === 'delete') {
    body = <div className="text-[12px] text-rose-600 italic">Deleted</div>;
  } else {
    const fields = row.changes ? Object.keys(row.changes) : [];
    body = (
      <ul className="text-[12px] text-slate-600 space-y-0.5">
        {fields.map((f) => {
          const diff = row.changes[f] || {};
          const fromVal = formatValue(f, diff.from, ctx);
          const toVal = formatValue(f, diff.to, ctx);
          return (
            <li key={f} className="leading-snug">
              <span className="font-semibold text-slate-700">{fieldLabel(f)}:</span>{' '}
              <span className="text-slate-500">{fromVal}</span>
              <span className="text-slate-400 mx-1">→</span>
              <span className="text-slate-800 font-medium">{toVal}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <li className="py-2.5 px-3 border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-[12px] font-semibold text-slate-800">{actor}</span>
        <span className="text-[10px] text-slate-400 tabular-nums" title={whenAbs}>{when}</span>
      </div>
      {body}
    </li>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Top-level component
// ──────────────────────────────────────────────────────────────────────

export default function ActivityLog({ tableName, recordId, title = 'Activity', limit = 50, className = '' }) {
  const { config } = useStore();
  const [rows, setRows] = useState(null); // null=loading, []=empty, [...]=loaded
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);

  // Status-key lookups depend on the current config-driven status definitions.
  // Concatenate all three status sets so we can resolve a label regardless of
  // which table the audit row came from. (Status keys are unique enough across
  // the three sets that collisions are unlikely; if they do collide, the
  // first set wins.)
  const ctx = useMemo(() => {
    const allStatuses = [
      ...getClientStatuses(config),
      ...getEstimateStatuses(config),
      ...getOperationsStatuses(config),
    ];
    return { allStatuses };
  }, [config]);

  useEffect(() => {
    if (!tableName || !recordId) return;
    let cancelled = false;
    setRows(null);
    setError(null);
    db.getAuditLog(tableName, recordId, { limit })
      .then((data) => {
        if (cancelled) return;
        setRows(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'Failed to load activity');
      });
    return () => { cancelled = true; };
  }, [tableName, recordId, limit]);

  return (
    <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden ${className}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors"
        aria-expanded={expanded}
      >
        <span className="text-[11px] font-bold tracking-wider text-slate-500 uppercase flex items-center gap-2">
          {title}
          {Array.isArray(rows) && (
            <span className="bg-slate-100 text-slate-600 text-xs font-medium px-2 py-0.5 rounded-full">
              {rows.length}
            </span>
          )}
        </span>
        <span
          aria-hidden="true"
          className="text-slate-400 text-sm transition-transform duration-200"
          style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
        >
          ›
        </span>
      </button>

      {expanded && (
        <div className="border-t border-slate-100">
          {rows === null && (
            <div className="px-4 py-6 text-center text-[12px] text-slate-400">
              Loading activity…
            </div>
          )}
          {error && (
            <div className="px-4 py-3 text-[12px] text-rose-600 bg-rose-50 border-t border-rose-100">
              {error}
            </div>
          )}
          {Array.isArray(rows) && rows.length === 0 && (
            <div className="px-4 py-6 text-center text-[12px] text-slate-400">
              No activity yet. Changes made to this record will appear here.
            </div>
          )}
          {Array.isArray(rows) && rows.length > 0 && (
            <ul>
              {rows.map((row) => (
                <ActivityEntry key={row.id} row={row} ctx={ctx} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
